import { ChangeEvent, useCallback, useState } from 'react';
import { Button, Modal as ModalDefault } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import Mask from '../../contants/Mask';
import HelperNumber from '../../helpers/HelperNumber';
import { ProposalService } from '../../services';
import LoadingSpinner from '../LoadingSpinner/LoadingSpinner';
import Modal from '../Modal';
import { max } from 'moment';

interface IProp {
  proposal: any;
  setProposal?: React.Dispatch<React.SetStateAction<any>>;
}

type FormValues = {
  value: string;
  percent: string;
  observation: string;
  file: FileList | null;
};

const ProposalDiscount = ({ proposal, setProposal }: IProp) => {
  const [valorProposal] = useState<number>(proposal?.cashValue ?? 0);
  const [valorDiscountFinsh, setValorDiscountFinsh] = useState<number>(0);
  const [valorBonusPotencial, setValorBonusPotencial] = useState<number>(proposal.earningPotential);
  const [valorBonusPotencialFinsh, setValorBonusPotencialFinsh] = useState<number>(proposal.earningPotential);
  const [proposalDiscountStatus, setProposalDiscountStatus] = useState<number>(proposal.statusDiscount);
  const [valorProposalFinsh, setValorProposalFinsh] = useState<number>(proposal?.cashValue ?? 0);
  const [fileProposal, setFileProposal] = useState<any>();
  const [showModal, setShowModal] = useState<boolean>(false);
  const [isLoad, setIsLoad] = useState<boolean>(false);
  const [isRequiredFields, setIsRequiredFields] = useState<boolean>(proposal.promotional ?? false);
  const maxDiscount = 20;
  const { formState: { errors }, register, handleSubmit, setValue, reset, watch } = useForm<FormValues>({
    defaultValues: {
      value: '0',
      percent: '0',
      observation: '',
      file: null,
    }, mode: 'onChange'
  });

  const observationFieldEmpty = watch('observation')?.length === 0;

  const handleRequiredFields = useCallback((percentValue: number) => {
    if (proposal.promotional) {
      setIsRequiredFields(proposal.promotional);
      return;
    }

    setIsRequiredFields(percentValue > 5);
  }, [proposal.promotional]);

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { files } = e.target;
    if (files && files.length > 0) {
      setFileProposal(files[0]);
    }
  };
  function calcularDescontoBalanceado(discount: number) {
    // Soma dos itens que receberão o desconto proporcional
    const somaItens = proposal.bdi + 
                      proposal.direct + 
                      proposal.rede + 
                      proposal.pontos;
    
    // Calcula a proporção de cada item em relação ao total
    const proporcaoBDI = proposal.bdi / somaItens;
    const proporcaoDirect = proposal.direct / somaItens;
    const proporcaoRede = proposal.rede / somaItens;
    const proporcaoPontos = proposal.pontos / somaItens;
    
    // Distribui o desconto proporcionalmente
    const descontoBDI = discount * proporcaoBDI;
    const descontoDirect = discount * proporcaoDirect;
    const descontoRede = discount * proporcaoRede;
    const descontoPontos = discount * proporcaoPontos;
    
    // Calcula os novos valores com o desconto aplicado
    const bdiProposto = proposal.bdi - descontoBDI;
    const directProposto = proposal.direct - descontoDirect;
    const redeProposto = proposal.rede - descontoRede;
    const pontosProposto = proposal.pontos - descontoPontos;
    
    // Calcula o novo total da rede
    const totalRedeProposto = redeProposto + pontosProposto;
    setValorBonusPotencialFinsh(directProposto);
  }
  function handleChangeValor(isValue: boolean, e: ChangeEvent<HTMLInputElement>) {
    const { value } = e.target;
    let masked = '0'

    if (isValue)
      masked = Mask.currencyMask(value) ?? '0'
    else {
      masked = HelperNumber.justNumber(value ?? '0');
      if (masked.length > 1)
        masked = masked.replace(/^0/, '')
    }

    let valueFormat = parseFloat(masked?.replace('.', '')?.replace(',', '.') ?? "0");
    if ((isValue && (valueFormat / valorProposal) * 100 > maxDiscount) || (!isValue && (valueFormat > maxDiscount))) {
      toast.warn(`Desconto máximo de ${maxDiscount}%`)
      isValue = false;
      valueFormat = maxDiscount;
      masked = maxDiscount.toFixed(2).replace('.00', '').trim(); 
    }
    handleRequiredFields(valueFormat);

    if (isValue) {
      setValue("value", masked);
      setValorDiscountFinsh(valueFormat);
      if (valueFormat > 0){
        setValorProposalFinsh(valorProposal - valueFormat);
        calcularDescontoBalanceado(valueFormat);
        const porcentagem = ((valueFormat / valorProposal) * 100).toFixed(2);
        setValue("percent", porcentagem.replace('.00', '').trim());
      }
      else{
        setValorProposalFinsh(valorProposal);
        setValorBonusPotencialFinsh(proposal.earningPotential);
        setValue("percent", '0');
      }
    } else {
      setValue("percent", masked);
      if (valueFormat > 0) {
        const valueDiscountFinsh = valorProposal * (valueFormat / 100);
        setValorDiscountFinsh(valueDiscountFinsh);
        setValorProposalFinsh(valorProposal - valueDiscountFinsh);
        calcularDescontoBalanceado(valueDiscountFinsh);
        setValue("value", HelperNumber.moneyFormat(valueDiscountFinsh).replace('R$', '').trim());
      } else {
        setValorDiscountFinsh(0);
        setValorProposalFinsh(valorProposal);
        setValorBonusPotencialFinsh(proposal.earningPotential);
        setValue("value", '0');
      }
    }
  }

  const onSubmit = async (data: FormValues) => {
    setIsLoad(true);
    const formData = new FormData();
    formData.append('id', proposal.id);
    formData.append('statusDiscount', '0');
    formData.append('justifyObs', '');
    formData.append('discountApprovalComments', data.observation);
    formData.append('requestDiscount', HelperNumber.read(valorDiscountFinsh));
    formData.append('fileProposal', fileProposal);

    try {
      await ProposalService.postDiscountDefault(formData);
      setShowModal(false);
      setProposalDiscountStatus(1);
      setProposal?.({
        ...proposal,
        statusDiscount: 1,
        proposalFileValid: false
      });
      reset();
      handleRequiredFields(0);
    } catch (error) {
      Modal.show({
        title: "Erro ao enviar Solicitação de Desconto",
        body: Modal.getErrorMessage(error, formData),
        showCancel: true,
        showConfirm: false,
        onCancelText: "Fechar"
      });
    } finally {
      setIsLoad(false);
    }
  };
  function showmodalclear() {
    setShowModal(prev => !prev);
    setValorProposalFinsh(valorProposal);
    setValorDiscountFinsh(0);
    handleRequiredFields(0);
    reset({
      observation: '',
      percent: '0',
      value: '0',
      file: null
    })
  }

  return (
    <>
      {
        ((proposalDiscountStatus ?? 0) === 1) &&
        <div className='alert alert-warning mt-2'>
          <i className='mdi mdi-alert-circle'></i> Desconto aguardando aprovação.
        </div>
      }
      {
        ((proposalDiscountStatus ?? 0) === 3) &&
        <div className='alert alert-danger mt-2'>
          <i className='mdi mdi-alert-circle'></i> Desconto Rejeitado: {proposal.discountReturnApprovalComments ?? "Sem detalhes"}
        </div>
      }
      {
        ((proposalDiscountStatus ?? 0) === 2) &&
        <div className='alert alert-success mt-2'>
          <i className='mdi mdi-alert-circle'></i> Desconto Aprovado
        </div>
      }
      {
        ((proposalDiscountStatus ?? 0) === 0 || (proposalDiscountStatus ?? 0) === 3) ?
          <>
            {
              isLoad &&
              <LoadingSpinner text='Carregando...' />
            }
            <button className='btn btn-dark waves-effect waves-light d-block minWidth200 mt-2'onClick={showmodalclear}>Solicitar Desconto</button>
            {
              <ModalDefault show={showModal}
                size={'lg'}
                className={''}
                animation={false}
                backdrop="static"
                keyboard={false}
                style={{ zIndex: '1040' }}
              >
                <ModalDefault.Header>
                  <ModalDefault.Title>{'Solicitação de Desconto'}</ModalDefault.Title>
                  <button type="button" className="close" data-dismiss="modal" aria-label="Fechar"
                    onClick={() => { setShowModal(false); reset(); }}>
                    <span aria-hidden="true">&times;</span>
                  </button>
                </ModalDefault.Header>
                <ModalDefault.Body>
                  <>
                    <form onSubmit={handleSubmit(onSubmit)} onReset={() => reset()} className="row">
                      <div className="col-12 col-md-6 mb-3">
                        <label htmlFor="valor">Desconto em Valor (R$)</label>
                        <input
                          type="text"
                          className='form-control'
                          id="valor"
                          {...register("value", {
                            onChange: (e) => handleChangeValor(true, e)
                          })}
                          placeholder="0.000,00"
                          aria-invalid={!!errors.value}
                        />
                      </div>
                      <div className="col-12 col-md-6 mb-3">
                        <label htmlFor="percent">Desconto em Porcentagem (%)</label>
                        <input
                          type="text"
                          className='form-control'
                          id="percent" // caso altere o id, verificar funcao de alteracao de valor
                          {...register("percent", {
                            onChange: (e) => handleChangeValor(false, e)
                          })}
                          placeholder="00"
                          aria-invalid={!!errors.percent}
                        />
                      </div>
                      {
                        (proposal.cashValue &&
                          Number(proposal.cashValue) > 0) ?
                          <>
                            <p className="col-12 mb-3 text-center">
                              <span style={{ fontSize: 10 }}>Valor originial da Proposta</span>
                              <span style={{ display: 'block', fontWeight: 'bold' }}>{HelperNumber.moneyFormat(valorProposal)}</span>
                            </p>
                            <p className="col-12 col-md-6 mb-3 text-center">
                              <span style={{ fontSize: 10 }}>Valor do Desconto:</span>
                              <span style={{ display: 'block', fontWeight: 'bold' }}>{HelperNumber.moneyFormat(valorDiscountFinsh)}</span>
                            </p>
                            <p className="col-12 col-md-6 mb-3 text-center">
                              <span style={{ fontSize: 10 }}>Valor Final da proposta:</span>
                              <span style={{ display: 'block', fontWeight: 'bold' }}>{HelperNumber.moneyFormat(valorProposalFinsh)}</span>
                            </p>
                            {
                              (proposal.bdi != null && proposal.direct != null && proposal.rede != null && proposal.pontos != null) &&
                              <>
                                <p className="col-12 col-md-6 mb-3 text-center">
                                  <span style={{ fontSize: 10 }}>Bônus potêncial atual:</span>
                                  <span style={{ display: 'block', fontWeight: 'bold' }}>{HelperNumber.moneyFormat(valorBonusPotencial)}</span>
                                </p>
                                <p className="col-12 col-md-6 mb-3 text-center">
                                  <span style={{ fontSize: 10 }}>Bônus potêncial novo:</span>
                                  <span style={{ display: 'block', fontWeight: 'bold' }}>{HelperNumber.moneyFormat(valorBonusPotencialFinsh)}</span>
                                </p>
                              </>
                            }
                          </> : <></>
                      }
                      <div className="col-12 mb-3">
                        <label htmlFor="observation">Observação:</label>
                        <textarea id="observation"
                          className="form-control"
                          {...register("observation", {
                            required: {
                              value: isRequiredFields && !fileProposal,
                              message: "Campo obrigatório!"
                            },
                            disabled: isLoad
                          })}
                          rows={2}
                          placeholder="Observações..."
                          aria-invalid={!!errors.observation}
                          aria-describedby="observation-error" />
                        {errors.observation &&
                          <span id="observation-error" className='text-danger'>{errors.observation.message}</span>}
                      </div>
                      <div className="col-12 mt-2">
                        <label htmlFor="fileProposta">Arquivo (Proposta):</label>
                        <input type="file"
                          id="fileProposta"
                          className="form-control my-0 mx-auto"
                          {...register("file", {
                            onChange: (e) => handleFileChange(e),
                            required: {
                              value: isRequiredFields && observationFieldEmpty,
                              message: "Campo obrigatório!"
                            },
                            disabled: isLoad
                          })}
                          aria-invalid={!!errors.file}
                          aria-describedby="fileProposta-error" />
                        {errors.file && <span id="fileProposta-error" className='text-danger'>{errors.file.message}</span>}
                      </div>
                    </form>
                  </>
                </ModalDefault.Body>
                <ModalDefault.Footer>
                  <Button className="btn btn-danger btnNO" onClick={() => { setShowModal(false); reset(); }}>Fechar</Button>
                  <Button className="btn btn-success btnOk" onClick={handleSubmit(onSubmit)}>Enviar Solicitação</Button>
                </ModalDefault.Footer>
              </ModalDefault>
            }
          </>
          :
          <></>
      }
    </>
  )
};

export default ProposalDiscount;
